import axios from 'axios';

// 创建axios实例
const api = axios.create({
  baseURL: '/api',
  timeout: 600000, // 10分钟超时，适用于大文件上传
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // FormData 请求：让浏览器自动设置 multipart Content-Type（含 boundary）
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // 可以在这里添加认证token
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response) {
      // 服务器返回错误
      const { status, data } = error.response;
      let message = '请求失败';
      
      if (data?.error) {
        message = data.error;
      } else if (status === 401) {
        message = '未授权，请重新登录';
      } else if (status === 403) {
        message = '权限不足';
      } else if (status === 404) {
        message = '资源不存在';
      } else if (status === 500) {
        message = '服务器内部错误';
      } else if (error.code === 'ECONNABORTED') {
        message = '请求超时，请检查网络连接';
      } else if (error.message === 'Network Error') {
        message = '网络错误，请检查网络连接';
      }
      
      console.error(`API Error [${status}]:`, data || error.message);
      return Promise.reject({ message, status, data });
    } else if (error.request) {
      // 请求发送但无响应
      console.error('No response received:', error.request);
      return Promise.reject({ message: '服务器无响应，请稍后重试' });
    } else {
      // 请求配置错误
      console.error('Request error:', error.message);
      return Promise.reject({ message: error.message });
    }
  }
);

// API接口定义
export const videoAPI = {
  // 获取分类列表
  getCategories: () => api.get('/categories'),
  
  // 获取视频列表
  getVideos: (params?: {
    category?: string | number;
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'ASC' | 'DESC';
    search?: string;
    featured?: boolean;
  }) => {
    return api.get('/videos', { params });
  },
  
  // 获取单个视频详情
  getVideo: (id: string | number) => api.get(`/videos/${id}`),
  
  // 上传视频
  uploadVideo: (formData: FormData, onProgress?: (progress: number) => void, cancelToken?: any) => {
    return api.post('/videos/upload', formData, {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  },
  
  // 获取热门视频
  getTrendingVideos: (limit?: number) => api.get('/videos/trending', { params: { limit } }),
  
  // 获取推荐视频
  getRecommendedVideos: (videoId: string | number) => api.get(`/videos/recommended/${videoId}`),
  
  // 搜索视频
  searchVideos: (query: string, category?: string | number, limit?: number) => {
    return api.get('/search', { params: { q: query, category, limit } });
  },
  
  // 获取单个视频详情（别名，与getVideo相同）
  getVideoById: (id: string | number) => api.get(`/videos/${id}`),
  
  // 获取相关视频
  getRelatedVideos: (videoId: string | number) => api.get(`/videos/related/${videoId}`),
  
  // 获取视频评论
  getVideoComments: (videoId: string | number, page?: number, limit?: number) => {
    return api.get(`/videos/${videoId}/comments`, { params: { page, limit } });
  },
  
  // 添加视频播放次数
  addVideoView: (videoId: string | number) => api.post(`/videos/${videoId}/view`),
  
  // 点赞视频
  likeVideo: (videoId: string | number) => api.post(`/videos/${videoId}/like`),
  
  // 取消点赞视频
  unlikeVideo: (videoId: string | number) => api.delete(`/videos/${videoId}/like`),
  
  // 添加评论
  addComment: (videoId: string | number, content: string) => 
    api.post(`/videos/${videoId}/comments`, { content }),
  
  // 删除评论
  deleteComment: (commentId: string | number) => api.delete(`/comments/${commentId}`),
  
  // 更新视频信息
  updateVideo: (id: string | number, data: {
    title?: string;
    description?: string;
    category_id?: number;
    is_featured?: boolean;
  }) => api.put(`/videos/${id}`, data),
  
  // 删除视频
  deleteVideo: (id: string | number) => api.delete(`/videos/${id}`),
  
  // 获取系统状态
  getSystemStatus: () => api.get('/status'),
  
  // 获取上传统计数据
  getUploadStats: () => api.get('/upload/stats'),

  // ===== 分片上传 API =====

  // 初始化分片上传会话
  initChunkUpload: (data: {
    fileName: string;
    fileSize: number;
    totalChunks: number;
    mimeType?: string;
  }) => api.post('/upload/chunk/init', data),

  // 上传单个分片 (1 chunk at a time to avoid memory issues)
  uploadChunk: (uploadId: string, chunkIndex: number, chunk: Blob, onProgress?: () => void) => {
    const formData = new FormData();
    formData.append('chunk', chunk, `chunk_${chunkIndex}`);
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', String(chunkIndex));
    return api.post('/upload/chunk/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: () => onProgress?.()
    });
  },

  // 完成合并
  completeChunkUpload: (data: {
    uploadId: string;
    title?: string;
    description?: string;
    category_id?: number | string;
    tags?: string;
    uploader?: string;
  }) => api.post('/upload/chunk/complete', data),

  // 查询上传状态（断点续传）
  getChunkUploadStatus: (uploadId: string) =>
    api.get(`/upload/chunk/status/${uploadId}`),

  // 取消/放弃上传
  abortChunkUpload: (uploadId: string) =>
    api.post(`/upload/chunk/abort/${uploadId}`),

  // ===== 缩略图/封面 API =====

  // 上传视频封面
  uploadThumbnail: (videoId: number | string, file: Blob | File) => {
    const formData = new FormData();
    formData.append('thumbnail', file);
    return api.post(`/upload/thumbnail/${videoId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // 获取视频封面信息
  getThumbnail: (videoId: number | string) =>
    api.get(`/upload/thumbnail/${videoId}`),
};

// 用户认证API
export const authAPI = {
  // 用户注册
  register: (data: {
    username: string;
    email: string;
    password: string;
    display_name?: string;
  }) => api.post('/auth/register', data),
  
  // 用户登录
  login: (data: { email: string; password: string }) => api.post('/auth/login', data),
  
  // 获取当前用户信息
  getCurrentUser: () => api.get('/auth/me'),
  
  // 修改密码
  changePassword: (data: { currentPassword: string; newPassword: string }) => 
    api.post('/auth/change-password', data),
  
  // 忘记密码
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  
  // 重置密码
  resetPassword: (data: { token: string; newPassword: string }) => 
    api.post('/auth/reset-password', data),
  
  // 更新用户资料
  updateProfile: (data: { display_name?: string; avatar_url?: string }) => 
    api.put('/auth/profile', data),
};

// 会员中心API
export const membershipAPI = {
  // 获取会员状态
  getMembershipStatus: () => api.get('/membership/status'),
  
  // 订阅会员（模拟支付）
  subscribe: (plan: 'monthly' | 'yearly') => api.post('/membership/subscribe', { plan }),
  
  // 获取支付记录
  getPaymentRecords: () => api.get('/membership/payments'),
};

// 工具函数：格式化文件大小
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes) return '未知大小';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// 工具函数：格式化视频时长
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || seconds === 0) return '';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// 工具函数：生成视频缩略图URL
export const getThumbnailUrl = (path?: string): string => {
  if (!path) return '/default-thumbnail.jpg';
  
  // 如果是相对路径，添加基础URL
  if (path.startsWith('/')) {
    return path;
  }
  
  return `/uploads/${path}`;
};

// 工具函数：生成视频播放URL
export const getVideoUrl = (path?: string): string => {
  if (!path) return '';
  
  // 如果是相对路径，添加基础URL
  if (path.startsWith('/')) {
    return path;
  }
  
  return `/uploads/${path}`;
};

export const isVideoFile = (file: File): boolean => {
  // 先检查 MIME 类型
  const videoTypes = [
    'video/mp4',
    'video/avi',
    'video/mov',
    'video/mkv',
    'video/webm',
    'video/flv',
    'video/wmv',
    'video/x-msvideo',
    'video/x-matroska',
    'video/quicktime',
    'video/x-flv',
    'video/x-ms-wmv',
    'video/mpeg',
    'video/ogg',
    'video/3gpp',
    'video/x-ms-asf',
    'application/octet-stream', // fallback for unknown types
  ];
  if (file.type && videoTypes.includes(file.type)) return true;
  // 回退：通过扩展名判断
  const dot = file.name.lastIndexOf('.');
  if (dot === -1) return false;
  const ext = file.name.toLowerCase().slice(dot);
  const videoExts = ['.mp4', '.avi', '.mov', '.mkv', '.webm', '.flv', '.wmv', '.mpeg', '.mpg', '.3gp', '.ts', '.mts'];
  return videoExts.includes(ext);
};

// 工具函数：获取文件扩展名
export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2).toLowerCase();
};

// 工具函数：生成唯一ID
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// 工具函数：防抖函数
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// 工具函数：节流函数
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// ===== 俱乐部 API =====
export const clubAPI = {
  getClubs: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    city?: string;
  }) => api.get('/clubs', { params }),
  
  getClub: (id: string | number) => api.get(`/clubs/${id}`),
  
  createClub: (data: any) => api.post('/clubs', data),
  
  updateClub: (id: string | number, data: any) => api.put(`/clubs/${id}`, data),
  
  deleteClub: (id: string | number) => api.delete(`/clubs/${id}`),
  
  addReview: (id: string | number, data: { rating: number; content: string; user_name?: string }) =>
    api.post(`/clubs/${id}/review`, data),
  
  getReviews: (id: string | number, params?: { page?: number; limit?: number }) =>
    api.get(`/clubs/${id}/reviews`, { params }),
  
  uploadImage: (formData: FormData) => api.post('/clubs/upload', formData),
  
  deleteImage: (filename: string) => api.delete(`/clubs/upload/${filename}`),
};

// 导出默认的axios实例
export default api;

// ===== 用户个人中心 API =====
export const userAPI = {
  // 观看历史
  getWatchHistory: (page?: number, limit?: number) => api.get('/user/watch-history', { params: { page, limit } }),
  addWatchHistory: (data: { video_id: number | string; video_title?: string; video_url?: string }) => api.post('/user/watch-history', data),
  clearWatchHistory: () => api.delete('/user/watch-history'),
  
  // 订单管理
  createOrder: (data: { items: any[]; total_amount: number; shipping_address?: string; contact_phone?: string; contact_name?: string; remark?: string }) => api.post('/user/orders', data),
  getOrders: (page?: number, limit?: number, status?: string) => api.get('/user/orders', { params: { page, limit, status } }),
  getOrderDetail: (id: number | string) => api.get(`/user/orders/${id}`),
  cancelOrder: (id: number | string) => api.put(`/user/orders/${id}/cancel`),
  
  // 教练信息发布
  getMyCoachPosts: () => api.get('/user/coach-posts'),
  createCoachPost: (data: any) => api.post('/user/coach-posts', data),
  updateCoachPost: (id: number | string, data: any) => api.put(`/user/coach-posts/${id}`, data),
  deleteCoachPost: (id: number | string) => api.delete(`/user/coach-posts/${id}`),
};